import type { Language } from '@magicschool/supabase/types';
import type { JoinRoomResponse } from 'app/api/s/rooms/join/route';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import { getLocaleForJoinCode, onJoinRoomSuccess } from './actions';

type JoinAction = (joinCode: string, roomStudentId?: string) => Promise<void>;

export type JoinStore = {
  setField: SetField<JoinStore>;
  loading: boolean;
  error: string;
  joinCode: string;
  name: string;
  autoJoin: JoinAction;
  getRoomDefaultLocale: (joinCode: string) => Promise<void>;
  autoLocale: Language | null;
};

const defaultState = {
  loading: true,
  joinCode: '',
  name: '',
  error: '',
  autoLocale: null,
};

export const createJoinStoreSlice = createStoreSlice('JoinStoreData', defaultState, ({ set, setField }) => ({
  setField,
  autoJoin: async (joinCode, roomStudentId) => {
    set({ loading: true, error: '' });
    const body = roomStudentId ? { joinCode, roomStudentId } : { joinCode, name: '' };
    await fetch<JoinRoomResponse>(`/api/s/rooms/join`, {
      method: 'POST',
      body: JSON.stringify(body),
      onSuccess: async ({ response, router }) => {
        const data = await response.json();
        await onJoinRoomSuccess(data.room_id, data.id);

        // if the room only has 1 tool, we navigate to that tool directly
        router.push(`/s/rooms/${data.room_id}/tools${data.nextRoomToolId ? `/${data.nextRoomToolId}` : ''}`);
      },
      responseErrorHandlers: {
        notFound: async ({ response, router }) => {
          const responseBody = await response.json<{ error: string }>();
          const error = responseBody.error === 'Record could not be found' ? 'join.room-code-not-found' : responseBody.error;
          set({ loading: false, error });
          router.push(`/s/join`);
        },
        badRequest: async ({ response, router }) => {
          const responseBody = await response.json<{ error: string }>();
          let error: string;
          if (responseBody.error.startsWith('supabase error:')) {
            error = roomStudentId ? 'join.error-rejoining-room' : 'join.room-code-not-found';
          } else {
            error = responseBody.error;
          }

          set({ loading: false, error });
          router.push(`/s/join`);
        },
        forbidden: async ({ response }) => {
          const result = await response.json<{ error: string }>();
          set({ loading: false, error: result.error });
        },
      },
    });
  },
  getRoomDefaultLocale: async (joinCode) => {
    const locale = await getLocaleForJoinCode(joinCode);
    set({ autoLocale: locale });
  },
}));
